import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calendar, Tag } from "lucide-react";
import { Eyebrow } from "@/components/Section";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SEO, articleJsonLd, breadcrumbJsonLd } from "@/components/SEO";
import { renderMarkdown } from "@/lib/markdown";

const fmtDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "de" ? "de-DE" : "en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export const Blog = () => {
  const { t, lang } = useI18n();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts").then(({ data }) => setPosts(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <SEO
        title="Blog · IT-Sicherheit & Managed Services"
        description="Aktuelle Themen rund um IT-Sicherheit, Managed Services, Compliance, Ransomware-Schutz und NIS-2. Insights aus der Praxis von IT-Servicepoint."
        keywords="IT Blog, IT-Sicherheit News, NIS-2, Ransomware, Cyber Security Blog, Managed Services Insights"
        jsonLd={breadcrumbJsonLd([{ name: "Start", path: "/" }, { name: "Blog", path: "/blog" }])}
      />
      <section className="relative overflow-hidden bg-soft border-b border-line">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
          <Eyebrow>Blog</Eyebrow>
          <h1 className="mt-6 font-display font-black text-5xl sm:text-7xl tracking-tightest text-ink">{t.blog.title}</h1>
          <p className="mt-6 max-w-2xl text-muted text-lg">{t.blog.sub}</p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-24">
          {loading ? (
            <p className="text-muted font-mono">{t.common.loading}</p>
          ) : posts.length === 0 ? (
            <p data-testid="blog-empty" className="text-muted font-mono">{t.blog.empty}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => {
                const title = lang === "de" ? p.title_de : (p.title_en || p.title_de);
                const excerpt = lang === "de" ? p.excerpt_de : (p.excerpt_en || p.excerpt_de);
                return (
                  <Link
                    key={p._id}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group block border border-line bg-white lift overflow-hidden shadow-soft"
                  >
                    {p.cover_image && (
                      <div className="aspect-[16/10] overflow-hidden bg-soft">
                        <img
                          src={p.cover_image}
                          alt={title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-7">
                      <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
                        <Calendar size={12} /> <time dateTime={p.created_at}>{fmtDate(p.created_at, lang)}</time>
                      </div>
                      <h2 className="mt-4 font-display font-bold text-xl text-ink group-hover:text-primary transition-colors">{title}</h2>
                      <p className="mt-3 text-muted text-sm leading-relaxed line-clamp-3">{excerpt}</p>
                      <div className="mt-5 flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-[0.2em]">
                        <span>{t.blog.read_more}</span>
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export const BlogPost = () => {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/posts/slug/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setError("not_found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-32 text-muted font-mono">{t.common.loading}</div>;
  if (error || !post)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <SEO title="404" noindex />
        <h2 className="font-display text-3xl font-bold text-ink">404</h2>
        <p className="text-muted mt-3">Beitrag nicht gefunden.</p>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-primary"><ArrowLeft size={16}/>{t.blog.back}</Link>
      </div>
    );

  const title = lang === "de" ? post.title_de : (post.title_en || post.title_de);
  const excerpt = lang === "de" ? post.excerpt_de : (post.excerpt_en || post.excerpt_de);
  const content = lang === "de" ? post.content_de : (post.content_en || post.content_de);

  return (
    <article data-testid="blog-post" className="relative bg-white">
      <SEO
        title={title}
        description={excerpt}
        image={post.cover_image}
        type="article"
        keywords={(post.tags || []).join(", ")}
        jsonLd={[
          articleJsonLd(post),
          breadcrumbJsonLd([
            { name: "Start", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <div className="max-w-4xl mx-auto px-6 sm:px-12 pt-12 sm:pt-16">
        <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-muted hover:text-primary">
          <ArrowLeft size={14}/> {t.blog.back}
        </Link>
      </div>
      <header className="max-w-4xl mx-auto px-6 sm:px-12 py-10 sm:py-14">
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted flex-wrap">
          <span className="inline-flex items-center gap-1.5"><Calendar size={12}/> <time dateTime={post.created_at}>{t.blog.published_on} {fmtDate(post.created_at, lang)}</time></span>
          {post.tags?.length > 0 && (
            <span className="inline-flex items-center gap-2"><Tag size={12}/> {post.tags.join(" · ")}</span>
          )}
        </div>
        <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl tracking-tightest leading-[1.02] text-ink">{title}</h1>
        {excerpt && <p className="mt-6 text-lg text-muted leading-relaxed">{excerpt}</p>}
      </header>
      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-6 sm:px-12 mb-12">
          <div className="aspect-[16/9] overflow-hidden bg-soft border border-line">
            <img src={post.cover_image} alt={title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-6 sm:px-12 pb-24">
        <div
          className="prose-itsp"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
          data-testid="blog-post-content"
        />
      </div>
    </article>
  );
};
